import React, { useEffect, useState } from "react";

import { DataGrid } from "@material-ui/data-grid";
import {
  IconButton,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tooltip,
  Box,
  Typography,
  Chip,
  Badge,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import StarOutlinedIcon from "@material-ui/icons/StarOutlined";
import AddIcon from "@material-ui/icons/Add";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Star from "@material-ui/icons/Star";
import StarHalf from "@material-ui/icons/StarHalf";
import StarBorder from "@material-ui/icons/StarBorder";

import { supabase } from "../../../../../supabase";

import { useAuth } from "../../../../../contexts/AuthContext";

import ModalCard from "../../../../shared/ModalCard";
import AgentAssistViewTicket from "../../../../admin-section/admin-agent-assist/AgentAssistViewTicket";
import AddAgentAssistTicketModal from "./AddAgentAssistTicketModal";
import RateAgentAssistServiceModal from "../shared/RateAgentAssistServiceModal";

import sqlToStandardDate from "../../../../shared/functions/sqlToStandardDate";
import formatTicketNumber from "../../../../shared/functions/formatTicketNumber";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  filterSection: {
    marginBottom: theme.spacing(2),
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 500,
  },
  gridContainer: {
    height: 500,
    width: "100%",
    "& .MuiDataGrid-root": {
      border: "none",
      backgroundColor: "#fff",
      borderRadius: theme.shape.borderRadius,
      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
    },
    "& .MuiDataGrid-cell": {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  statusChip: {
    fontWeight: 500,
  },
  filterChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500,
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  viewButton: {
    minWidth: "auto",
    padding: theme.spacing(0.5),
  },
  badge: {
    margin: 5,
    "& .MuiBadge-badge": {
      top: 0,
    },
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
  },
  filterLabel: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * Checks if there are any unread messages in the activity.
 *
 * @param {Array<{read_user: string|null, read_admin: string|null}>} activity - The array of activity objects, where each object has read status fields.
 * @param {boolean} isAdmin - A flag indicating if the check is for admin (true) or user (false).
 * @returns {boolean} - Returns true if there is at least one unread message, otherwise false.
 */
function hasUnread(activity, isAdmin) {
  const field = isAdmin ? "read_admin" : "read_user";

  return activity?.some(item => item[field] === null);
}

/**
 * Component to display a list of leads for a supplier, with the ability to filter
 * by status and view a lead's details.
 *
 * @param {object} props - The component props.
 * @param {object} props.Supplier - The supplier object.
 * @returns {React.ReactElement} - The AgentAssist component.
 */
export default function AgentAssist({ Supplier }) {
  const classes = useStyles();

  // Get the current authenticated user
  const { currentUser } = useAuth();

  // State to manage loading status
  const [loading, setLoading] = useState(false);

  // State to store the list of leads
  const [tickets, setTickets] = useState([]);

  // State to manage filter options for displaying leads
  const [displayFilters, setDisplayFilters] = useState({
    open: true,
    inProgress: true,
    closed: true,
  });

  // State to manage the modal for viewing a lead
  const [viewTicket, setViewTicket] = useState({
    isOpen: false,
    id: null,
  });

  // State to manage the modal for rating the service
  const [rateService, setRateService] = useState({
    isOpen: false,
    id: null,
  });

  // State for add ticket modal
  const [addTicketModal, setAddTicketModal] = useState(false);

  // Function to handle viewing a ticket
  const handleViewTicket = ticketId => {
    setViewTicket({ id: ticketId, isOpen: true });
  };

  // Function to close the view lead modal
  const closeViewTicket = () => {
    setViewTicket({ id: null, isOpen: false });
    getData(true);
  };

  const toggleAddTicketModal = () => {
    setAddTicketModal(prev => !prev);
    getData();
  };

  const toggleRateServiceModal = ticketId => {
    setRateService({ id: ticketId, isOpen: true });
  };

  const closeRateServiceModal = () => {
    setRateService({ id: null, isOpen: false });
    getData();
  };

  // Function to fetch tickets data from the database
  const getData = async (showLoading = true) => {
    if (showLoading) setLoading(true); // Set loading to true before fetching data

    const { data: assistTickets, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("agent_assist")
      .select("*, agent_assist_activity(read_user, read_admin)")
      .eq("auth_id", currentUser.auth_id) // Filter by the current user's dealer code
      .eq("supplier", Supplier) // Filter by the supplier
      .eq("is_active", true)
      .order("created_at", { ascending: false }); // Order by date submitted, newest first

    if (error) {
      console.error(error); // Log any errors
    } else {
      const formattedTickets = assistTickets.map(ticket => ({
        ...ticket,
        id: ticket.id,
        ticket_number: formatTicketNumber(ticket.id, "ZAA"),
        unread: hasUnread(ticket.sales_assist_activity, currentUser.rank === 1),
      }));
      setTickets(formattedTickets); // Update leads state with the fetched data
    }
    if (showLoading) setLoading(false); // Set loading to false after fetching data
  };

  const getStatusColor = status => {
    switch (status) {
      case "New":
        return "primary";
      case "In Progress":
        return "secondary";
      case "Closed":
        return "default";
      default:
        return "default";
    }
  };

  // Filter leads based on the selected filter options
  const openFilter = displayFilters.open ? "Open" : undefined;
  const inProgressFilter = displayFilters.inProgress
    ? "In Progress"
    : undefined;
  const closedFilter = displayFilters.closed ? "Closed" : undefined;

  const displayTickets = tickets?.filter(
    lead =>
      lead.status === openFilter ||
      lead.status === inProgressFilter ||
      lead.status === closedFilter ||
      lead.status === null
  );

  // Columns configuration for the DataGrid
  const columns = [
    {
      field: "View/Upload",
      width: 120,
      renderCell: params => (
        <div>
          <Tooltip title="View Ticket Details">
            <Badge
              color="secondary"
              variant="dot"
              invisible={!params.row.unread}
              className={classes.badge}
            >
              <Button
                variant="outlined"
                size="small"
                className={classes.viewButton}
                onClick={() => {
                  handleViewTicket(params.id);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </Button>
            </Badge>
          </Tooltip>
          {params.row.status === "Closed" &&
            params.row.rating_star === null && (
              <Tooltip title="Rate Service">
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.viewButton}
                  onClick={() => toggleRateServiceModal(params.id)}
                >
                  <StarOutlinedIcon fontSize="small" />
                </Button>
              </Tooltip>
            )}
          {params.row.status === "Closed" && params.row.rating_star && (
            <Tooltip
              title={`Your Review: ${params.row.rating_star}/5.\n\n${params.row.rating_comment}`}
            >
              <Button
                variant="outlined"
                size="small"
                className={classes.viewButton}
                disabled
              >
                {params.row.rating_star <= 2 && <StarBorder fontSize="small" />}
                {params.row.rating_star > 2 && params.row.rating_star <= 4 && (
                  <StarHalf fontSize="small" />
                )}
                {params.row.rating_star > 4 && <Star fontSize="small" />}
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      field: "ticket_number",
      headerName: "Ticket #",
      width: 120,
      renderCell: params => <Box fontWeight="medium">{params.value}</Box>,
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 120,
      renderCell: params => (
        <Typography variant="body2" color="textSecondary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      width: 170,
      renderCell: params => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: params => (
        <Chip
          label={params.value || "N/A"}
          size="small"
          color={getStatusColor(params.value)}
          className={classes.statusChip}
          style={{
            color: `${params.value === "Closed" ? "#3b3b3b" : "white"}`,
          }}
        />
      ),
    },
    {
      field: "status_feedback",
      headerName: "Status Feedback",
      width: 220,
      renderCell: params => (
        <Typography variant="body2" color="textSecondary" noWrap>
          {params.value || "—"}
        </Typography>
      ),
    },
    { field: "ticket_number", headerName: "Ticket Number", width: 110 },
    { field: "created_at", headerName: "Created At", width: 110 },
    { field: "category", headerName: "Category", width: 170 },
    { field: "status", headerName: "Status", width: 180 },
    { field: "status_feedback", headerName: "Status Feedback", width: 250 },
    {
      field: "supplier_ticket_number",
      headerName: "Supplier Ticket",
      width: 250,
    },
    { field: "user_note", headerName: "User Note", width: 250 },
  ];

  // Transform the leads data into rows for the DataGrid
  const rows = displayTickets?.map(row => {
    const statusFeedback =
      row.status === "In Progress"
        ? row.ticket_in_progress_status
        : row.ticket_closed_status;

    const date = sqlToStandardDate(row.created_at); // Convert SQL date to standard date format

    return {
      id: row.id,
      ticket_number: row.ticket_number,
      created_at: date,
      category: row.category,
      status: row.status,
      status_feedback: statusFeedback,
      supporting_document_ref: row.supporting_document_ref,
      user_note: row.user_note,
      unread: row.unread,
      supplier_ticket_number: row.supplier_ticket_number,
      rating_star: row.rating_star,
      rating_comment: row.rating_comment,
    };
  });

  // Fetch leads data when the component mounts or when the Supplier prop changes
  useEffect(() => {
    getData();
  }, [Supplier]);

  useEffect(() => {
    const subscription = supabase
      .channel("agent_assist_activity_channel_3")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: process.env.REACT_APP_SB_SCHEMA,
          table: "agent_assist_activity",
        },
        () => getData(false)
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [viewTicket.isOpen, addTicketModal]);

  const handleFilterClick = event => {
    const { name } = event.target;
    setDisplayFilters(old => ({
      ...old,
      [name]: !old[name],
    }));
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography variant="h6" className={classes.title}>
          Agent Support Tickets
          <Tooltip title="View and manage your support tickets">
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={toggleAddTicketModal}
        >
          New Ticket
        </Button>
      </div>

      <Paper variant="outlined" className={classes.filterSection}>
        <Box p={2}>
          <div className={classes.filterContainer}>
            <Typography variant="body2" className={classes.filterLabel}>
              Filter by status:
            </Typography>
            <FormGroup row>
              <FormControlLabel
                label={
                  <Chip
                    label="Open"
                    color={displayFilters.open ? "primary" : "default"}
                    size="small"
                    className={classes.filterChip}
                  />
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={displayFilters.open}
                    onClick={handleFilterClick}
                    name="open"
                    size="small"
                  />
                }
              />
              <FormControlLabel
                label={
                  <Chip
                    label="In Progress"
                    color={displayFilters.inProgress ? "secondary" : "default"}
                    size="small"
                    className={classes.filterChip}
                    style={{
                      color: "white",
                    }}
                  />
                }
                control={
                  <Checkbox
                    color="secondary"
                    checked={displayFilters.inProgress}
                    onClick={handleFilterClick}
                    name="inProgress"
                    size="small"
                  />
                }
              />
              <FormControlLabel
                label={
                  <Chip
                    label="Closed"
                    variant={displayFilters.closed ? "default" : "outlined"}
                    size="small"
                    className={classes.filterChip}
                    style={{
                      color: "#3b3b3b",
                    }}
                  />
                }
                control={
                  <Checkbox
                    color="default"
                    checked={displayFilters.closed}
                    onClick={handleFilterClick}
                    name="closed"
                    size="small"
                  />
                }
              />
            </FormGroup>
          </div>
        </Box>
      </Paper>

      <Box mt={2} className={classes.gridContainer}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={7}
          autoHeight
          disableSelectionOnClick
          density="standard"
        />
      </Box>

      {/* Modal for viewing a lead */}
      <ModalCard
        open={viewTicket.isOpen}
        onClose={closeViewTicket}
        closeButtonText="Close"
      >
        <AgentAssistViewTicket ticketId={viewTicket.id} />
      </ModalCard>

      {/* Modal for rating service */}
      <ModalCard open={rateService.isOpen}>
        <RateAgentAssistServiceModal
          ticketId={rateService.id}
          onClose={closeRateServiceModal}
          table="agent_assist"
        />
      </ModalCard>

      {/* Modal for adding a new ticket */}
      <ModalCard open={addTicketModal} onClose={toggleAddTicketModal}>
        <AddAgentAssistTicketModal
          supplier={Supplier}
          onClose={toggleAddTicketModal}
          open={addTicketModal}
        />
      </ModalCard>
    </>
  );
}
